import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authAPI } from '../../modules/auth/api/authAPI';
import type { MenuPermissionItem } from '../../utils/menuPermissions';

// Enhanced error logging utility
const logAuthError = (action: string, error: any, _context?: any) => {
    const errorMessage = error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Unknown error';

    return errorMessage;
};

// Retry utility for network failures
const withRetry = async <T>(
    operation: () => Promise<T>,
    maxRetries: number = 2,
    delay: number = 1000
): Promise<T> => {
    let lastError: any;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await operation();
        } catch (error: any) {
            lastError = error;

            // Don't retry on authentication errors (401, 403)
            if (error?.response?.status === 401 || error?.response?.status === 403) {
                throw error;
            }

            // Don't retry on client errors (400-499) except for network issues
            if (error?.response?.status >= 400 && error?.response?.status < 500 && error?.response?.status !== 408) {
                throw error;
            }

            if (attempt < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, delay));
                delay *= 2; // Exponential backoff
            }
        }
    }

    throw lastError;
};

// Type guard function for error validation
const isValidErrorString = (error: string | null | undefined): error is string => {
    return typeof error === 'string' && error.length > 0;
};

export interface User {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    full_name: string;
    role: 1 | 2 | 3; // SUPERADMIN | SUPPORTCOORDINATOR | TEAMLEADER (primary/precedence)
    roles: number[]; // All assigned roles
    role_display: string;
    is_active: boolean;
    is_superuser?: boolean;
    user_permissions?: number[];
    menu_items?: number[];
    effective_menu_items?: MenuPermissionItem[];
    profile_image?: string | null;
    profile_image_url?: string | null;
    notes?: string | null;
    organization?: {
        favicon_url?: string | null;
        logo_url?: string | null;
        name?: string;
    };
}

export interface AuthState {
    user: User | null;
    token: string | null;
    refreshToken: string | null;
    isAuthenticated: boolean;
    loading: boolean;
    error: string | null;
    initialized: boolean;
    rememberMe: boolean;
    savedCredentials: {
        username: string;
        password: string;
    } | null;
}

import { credentialSecurity } from '../../utils/credentialSecurity';

const initialState: AuthState = {
    user: null,
    token: localStorage.getItem('access_token'),
    refreshToken: localStorage.getItem('refresh_token'),
    isAuthenticated: false, // Don't assume token is valid until verified
    loading: false,
    error: null,
    initialized: false,
    rememberMe: credentialSecurity.hasCredentials(),
    savedCredentials: credentialSecurity.getSavedCredentials(),
};

// Async thunks
export const checkAuthStatus = createAsyncThunk(
    'auth/checkAuthStatus',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            const refreshToken = localStorage.getItem('refresh_token');

            if (token && refreshToken) {
                return {
                    access: token,
                    refresh: refreshToken,
                    user: null
                };
            }

            const errorMessage = 'No stored authentication tokens found';
            return rejectWithValue(errorMessage);
        } catch (error: any) {
            const errorMessage = logAuthError('status check', error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const login = createAsyncThunk(
    'auth/login',
    async (credentials: { email: string; password: string; rememberMe?: boolean }, { rejectWithValue }) => {
        try {
            const response = await withRetry(
                () => authAPI.login(credentials),
                1 // Only retry once for login
            );

            // Store tokens in localStorage
            localStorage.setItem('access_token', response.access);
            localStorage.setItem('refresh_token', response.refresh);

            // Handle remember me functionality
            if (credentials.rememberMe) {
                credentialSecurity.saveCredentials({
                    username: credentials.email,
                    password: credentials.password
                });
            } else {
                credentialSecurity.clearCredentials();
            }

            return { ...response, rememberMe: credentials.rememberMe || false };
        } catch (error: any) {
            const errorMessage = logAuthError('login', error, { username: credentials.email });

            return rejectWithValue(errorMessage);
        }
    }
);

export const googleLogin = createAsyncThunk(
    'auth/googleLogin',
    async (idToken: string, { rejectWithValue }) => {
        try {
            const response = await authAPI.googleAuth(idToken);
            localStorage.setItem('access_token', response.access);
            localStorage.setItem('refresh_token', response.refresh);
            return { ...response, rememberMe: false };
        } catch (error: any) {
            const errorMessage = logAuthError('google login', error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const microsoftLogin = createAsyncThunk(
    'auth/microsoftLogin',
    async (idToken: string, { rejectWithValue }) => {
        try {
            const response = await authAPI.microsoftAuth(idToken);
            localStorage.setItem('access_token', response.access);
            localStorage.setItem('refresh_token', response.refresh);
            return { ...response, rememberMe: false };
        } catch (error: any) {
            const errorMessage = logAuthError('microsoft login', error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const refreshAccessToken = createAsyncThunk(
    'auth/refreshToken',
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { auth: AuthState };
            const refreshToken = state.auth.refreshToken;

            if (!refreshToken) {
                const errorMessage = 'No refresh token available for token refresh';
                throw new Error(errorMessage);
            }

            const response = await withRetry(
                () => authAPI.refreshToken(refreshToken),
                2 // Retry token refresh up to 2 times
            );

            // Store new access token
            localStorage.setItem('access_token', response.access);

            return response;
        } catch (error: any) {
            // Clear tokens on refresh failure
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');

            const errorMessage = logAuthError('token refresh', error);

            return rejectWithValue(errorMessage);
        }
    }
);

export const logout = createAsyncThunk(
    'auth/logout',
    async (_, { getState }) => {
        try {
            const state = getState() as { auth: AuthState };
            const refreshToken = state.auth.refreshToken;

            if (refreshToken) {
                await withRetry(
                    () => authAPI.logout(refreshToken),
                    1 // Only retry logout once
                );
            }
        } catch (error: any) {
            // Continue with logout even if API call fails
            logAuthError('logout API', error);
        } finally {
            // Always clear local storage
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
        }
    }
);

export const fetchCurrentUser = createAsyncThunk(
    'auth/fetchCurrentUser',
    async (_, { rejectWithValue }) => {
        try {
            const userData = await withRetry(
                () => authAPI.getCurrentUser(),
                1
            );
            return userData;
        } catch (error: any) {
            const errorMessage = logAuthError('fetch current user', error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const initializeAuth = createAsyncThunk(
    'auth/initialize',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const token = localStorage.getItem('access_token');
            const refreshToken = localStorage.getItem('refresh_token');

            if (!token || !refreshToken) {
                const errorMessage = 'No stored authentication tokens found during initialization';
                return rejectWithValue(errorMessage);
            }

            try {
                const userData = await withRetry(() => authAPI.getCurrentUser(), 1);

                return {
                    user: userData,
                    access: token,
                    refresh: refreshToken
                };
            } catch (userFetchError: any) {
                try {
                    await dispatch(refreshAccessToken()).unwrap();

                    const newToken = localStorage.getItem('access_token');
                    const userData = await authAPI.getCurrentUser();

                    return {
                        user: userData,
                        access: newToken!,
                        refresh: refreshToken
                    };
                } catch (refreshError: any) {
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('refresh_token');
                    logAuthError('initialization refresh', refreshError);
                    throw refreshError;
                }
            }
        } catch (error: any) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');

            const errorMessage = logAuthError('initialization', error);

            return rejectWithValue(errorMessage);
        }
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearInitializationError: (state) => {
            // Clear initialization-related errors but keep login errors
            if (isValidErrorString(state.error) && (
                state.error.includes('initialization') ||
                state.error.includes('No stored authentication tokens')
            )) {
                state.error = null;
            }
        },
        setUser: (state, action: PayloadAction<User>) => {
            state.user = action.payload;
        },
        setCredentials: (state, action: PayloadAction<{ user: User; token: string; refreshToken: string }>) => {
            state.user = action.payload.user;
            state.token = action.payload.token;
            state.refreshToken = action.payload.refreshToken;
            state.isAuthenticated = true;
            state.error = null;
            state.loading = false;
            state.initialized = true;
            localStorage.setItem('access_token', action.payload.token);
            localStorage.setItem('refresh_token', action.payload.refreshToken);
        },
        clearCredentials: (state) => {
            state.user = null;
            state.token = null;
            state.refreshToken = null;
            state.isAuthenticated = false;
            state.error = null;
        },
        setSavedCredentials: (state, action: PayloadAction<{ username: string; password: string } | null>) => {
            state.savedCredentials = action.payload;
        },
        setRememberMe: (state, action: PayloadAction<boolean>) => {
            state.rememberMe = action.payload;
            if (!action.payload) {
                state.savedCredentials = null;
                credentialSecurity.clearCredentials();
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Login
            .addCase(login.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.loading = false;
                state.initialized = true;
                state.user = action.payload.user;
                state.token = action.payload.access;
                state.refreshToken = action.payload.refresh;
                state.isAuthenticated = true;
                state.error = null;
                state.rememberMe = action.payload.rememberMe;
                if (action.payload.rememberMe) {
                    state.savedCredentials = credentialSecurity.getSavedCredentials();
                }
            })
            .addCase(login.rejected, (state, action) => {
                state.loading = false;
                state.initialized = true;
                state.error = action.payload as string;
                state.isAuthenticated = false;
                state.user = null;
                state.token = null;
                state.refreshToken = null;
            })

            // Google Login
            .addCase(googleLogin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(googleLogin.fulfilled, (state, action) => {
                state.loading = false;
                state.initialized = true;
                state.user = action.payload.user;
                state.token = action.payload.access;
                state.refreshToken = action.payload.refresh;
                state.isAuthenticated = true;
                state.error = null;
                state.rememberMe = false;
            })
            .addCase(googleLogin.rejected, (state, action) => {
                state.loading = false;
                state.initialized = true;
                state.error = action.payload as string;
                state.isAuthenticated = false;
                state.user = null;
                state.token = null;
                state.refreshToken = null;
            })

            // Microsoft Login
            .addCase(microsoftLogin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(microsoftLogin.fulfilled, (state, action) => {
                state.loading = false;
                state.initialized = true;
                state.user = action.payload.user;
                state.token = action.payload.access;
                state.refreshToken = action.payload.refresh;
                state.isAuthenticated = true;
                state.error = null;
                state.rememberMe = false;
            })
            .addCase(microsoftLogin.rejected, (state, action) => {
                state.loading = false;
                state.initialized = true;
                state.error = action.payload as string;
                state.isAuthenticated = false;
                state.user = null;
                state.token = null;
                state.refreshToken = null;
            })

            // Refresh token
            .addCase(refreshAccessToken.pending, (_state) => {
                // Don't set loading to true for refresh as it's usually background
            })
            .addCase(refreshAccessToken.fulfilled, (state, action) => {
                state.token = action.payload.access;
                state.error = null;
            })
            .addCase(refreshAccessToken.rejected, (state, action) => {
                state.user = null;
                state.token = null;
                state.refreshToken = null;
                state.isAuthenticated = false;
                state.error = 'Session expired. Please login again.';
            })

            // Logout
            .addCase(logout.pending, (state) => {
                state.loading = true;
            })
            .addCase(logout.fulfilled, (state) => {
                state.user = null;
                state.token = null;
                state.refreshToken = null;
                state.isAuthenticated = false;
                state.error = null;
                state.loading = false;
                state.initialized = true;
                // Keep saved credentials if remember me is enabled
                if (!state.rememberMe) {
                    state.savedCredentials = null;
                }
            })
            .addCase(logout.rejected, (state, _action) => {
                // Even if logout API fails, clear the local state
                state.user = null;
                state.token = null;
                state.refreshToken = null;
                state.isAuthenticated = false;
                state.error = null;
                state.loading = false;
                state.initialized = true;
            })

            // Check auth status
            .addCase(checkAuthStatus.pending, (state) => {
                state.loading = true;
            })
            .addCase(checkAuthStatus.fulfilled, (state, action) => {
                state.loading = false;
                state.token = action.payload.access;
                state.refreshToken = action.payload.refresh;
                state.isAuthenticated = true;
                // Note: user will be null until we implement user data fetching
                state.user = action.payload.user;
                state.error = null;
            })
            .addCase(checkAuthStatus.rejected, (state, action) => {
                state.loading = false;
                state.isAuthenticated = false;
                state.user = null;
                state.token = null;
                state.refreshToken = null;
                state.error = action.payload as string;
            })

            // Fetch current user
            .addCase(fetchCurrentUser.pending, (_state) => {
                // Don't set loading to true for user fetch as it's usually background
            })
            .addCase(fetchCurrentUser.fulfilled, (state, action) => {
                state.user = action.payload;
                state.error = null;
            })
            .addCase(fetchCurrentUser.rejected, (state, action) => {
                // Don't clear authentication on user fetch failure
                state.error = action.payload as string;
            })

            // Initialize auth
            .addCase(initializeAuth.pending, (state) => {
                state.loading = true;
                state.initialized = false;
            })
            .addCase(initializeAuth.fulfilled, (state, action) => {
                state.loading = false;
                state.initialized = true;
                state.user = action.payload.user;
                state.token = action.payload.access;
                state.refreshToken = action.payload.refresh;
                state.isAuthenticated = true;
                state.error = null;
            })
            .addCase(initializeAuth.rejected, (state, action) => {
                state.loading = false;
                state.initialized = true;
                state.isAuthenticated = false;
                state.user = null;
                state.token = null;
                state.refreshToken = null;
                // Don't set error for missing tokens during initialization - this is normal
                const errorMessage = action.payload as string;
                if (!errorMessage.includes('No stored authentication tokens found')) {
                    state.error = errorMessage;
                } else {
                    state.error = null; // Clear any previous errors
                }
            });
    },
});

export const { clearError, clearInitializationError, setCredentials, clearCredentials, setSavedCredentials, setRememberMe, setUser } = authSlice.actions;
export default authSlice.reducer;
